import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Layout
import MainLayout from "./layouts/MainLayout";

// Axiom AI Assistant
import AxiomChat from "./components/AxiomChat";

// Existing Pages
import Home from "./pages/Home";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import OrgLogin from "./pages/OrgLogin";
import OrgDashboard from "./pages/OrgDashboard";
import Registry from "./pages/Registry";
import Verify from "./pages/Verify";
import Tools from "./pages/Tools";
import ToolRunner from "./pages/ToolRunner";
import ReportView from "./pages/ReportView";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import CertificationScope from "./pages/CertificationScope";
import Eligibility from "./pages/Eligibility";
import Enforcement from "./pages/Enforcement";
import Revocation from "./pages/Revocation";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Apply from "./pages/Apply";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Documentation from "./pages/Documentation";

// AI Risk Assessment Funnel Pages
import AssessmentForm from "./pages/AssessmentForm";
import AssessmentResults from "./pages/AssessmentResultsNew";
import AssessmentPurchaseSuccess from "./pages/AssessmentPurchaseSuccess";
import Start from "./pages/Start";

// Commercial Layer Pages
import DocumentationSystem from "./pages/DocumentationSystem";
import AssessmentProduct from "./pages/AssessmentProduct";
import AssessmentProductSuccess from "./pages/AssessmentProductSuccess";
import FreeTools from "./pages/FreeTools";
import PolicyGenerator from "./pages/PolicyGenerator";
import InventoryBuilder from "./pages/InventoryBuilder";
import GovernanceChecklist from "./pages/GovernanceChecklist";

// Article CMS Pages
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";

// Standards Pages (Horizons Import)
import StandardsIndexPage from "./pages/standards/StandardsIndexPage";
import StandardAIABND from "./pages/standards/StandardAIABND";
import StandardAIHITL from "./pages/standards/StandardAIHITL";
import StandardAIACCT from "./pages/standards/StandardAIACCT";
import StandardAIREAD from "./pages/standards/StandardAIREAD";
import StandardAIPERF from "./pages/standards/StandardAIPERF";
import StandardAIROI from "./pages/standards/StandardAIROI";
import StandardAIDISC from "./pages/standards/StandardAIDISC";
import AccountabilityBoundariesPacketTemplate from "./pages/standards/AccountabilityBoundariesPacketTemplate";
import StandardsTaxonomy from "./pages/standards/StandardsTaxonomy";
import StandardsVersioningPolicy from "./pages/standards/StandardsVersioningPolicy";
import StandardsRevisionAndUpdatePolicy from "./pages/standards/StandardsRevisionAndUpdatePolicy";
import StandardsAlignmentAndPrinciples from "./pages/standards/StandardsAlignmentAndPrinciples";
import ArchivedStandards from "./pages/standards/ArchivedStandards";
import FrameworkAlignment from "./pages/standards/FrameworkAlignment";

// Declaration and Registry Pages (Horizons Import)
import WhatIsDeclaration from "./pages/standards/WhatIsDeclaration";
import WhatCertificationMeansForClients from "./pages/standards/WhatCertificationMeansForClients";
import WhatCertificationMeansForBuyers from "./pages/standards/WhatCertificationMeansForBuyers";
import DeclarationPage from "./pages/standards/DeclarationPage";
import PricingAndPrograms from "./pages/standards/PricingAndPrograms";
import EligibilityRequirements from "./pages/standards/EligibilityRequirements";
import RegistryStatusDefinitions from "./pages/standards/RegistryStatusDefinitions";
import RegistryRelationship from "./pages/standards/RegistryRelationship";
import RegistryMethodologyAndStatusDefinitions from "./pages/standards/RegistryMethodologyAndStatusDefinitions";
import VerifyCredential from "./pages/standards/VerifyCredential";
import MarkUsagePolicy from "./pages/standards/MarkUsagePolicy";
import DeclarationBadgeUsagePolicy from "./pages/standards/DeclarationBadgeUsagePolicy";
import SubmitAConcern from "./pages/standards/SubmitAConcern";
import EnforcementRevocationPolicy from "./pages/standards/EnforcementRevocationPolicy";
import DeclarationEnforcementAndStatus from "./pages/standards/DeclarationEnforcementAndStatus";
import SuspensionAndRevocationPolicy from "./pages/standards/SuspensionAndRevocationPolicy";

// Governance Pages (Horizons Import)
import AboutPage from "./pages/standards/AboutPage";
import GovernanceAndAuthority from "./pages/standards/GovernanceAndAuthority";
import ConflictOfInterestPolicy from "./pages/standards/ConflictOfInterestPolicy";
import IndependenceStatement from "./pages/standards/IndependenceStatement";
import DeclarationScopeAndLimitations from "./pages/standards/DeclarationScopeAndLimitations";
import ScopeOfApplicability from "./pages/standards/ScopeOfApplicability";
import WhyTheseStandardsExist from "./pages/standards/WhyTheseStandardsExist";
import TermsOfDeclaration from "./pages/standards/TermsOfDeclaration";
import TermsOfService from "./pages/standards/TermsOfService";
import PrivacyPolicyHorizons from "./pages/standards/PrivacyPolicy";

// Educational Pages (Horizons Import)
import WhatIsAlignmentDeclaration from "./pages/standards/WhatIsAlignmentDeclaration";
import UsingDeclarationInProposals from "./pages/standards/UsingDeclarationInProposals";
import DeclarationFAQs from "./pages/standards/DeclarationFAQs";
import MethodologyPage from "./pages/standards/MethodologyPage";
import StandardsAlignmentDeclaration from "./pages/standards/StandardsAlignmentDeclaration";

// Wrapper component for standards pages with layout
const WithLayout = ({ component: Component }) => (
  <MainLayout>
    <Component />
  </MainLayout>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes (keep existing layout) */}
          <Route path="/" element={<Home />} />
          <Route path="/start" element={<Start />} />
          <Route path="/registry" element={<Registry />} />
          <Route path="/verify/:certificationId" element={<Verify />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/tools/:toolId/run" element={<ToolRunner />} />
          <Route path="/reports/:reportId" element={<ReportView />} />
          
          {/* Application Flow */}
          <Route path="/apply" element={<Apply />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          
          {/* AI Risk Assessment Funnel */}
          <Route path="/assessment" element={<Navigate to="/assessment/form" replace />} />
          <Route path="/assessment/form" element={<AssessmentForm />} />
          <Route path="/assessment/results/:assessmentId" element={<AssessmentResults />} />
          <Route path="/assessment/purchase-success" element={<AssessmentPurchaseSuccess />} />
          
          {/* ========== COMMERCIAL LAYER ROUTES ========== */}
          <Route path="/documentation-system" element={<DocumentationSystem />} />
          <Route path="/assessment-product" element={<AssessmentProduct />} />
          <Route path="/assessment-product/success" element={<AssessmentProductSuccess />} />
          <Route path="/free-tools" element={<FreeTools />} />
          <Route path="/tools/policy-generator" element={<PolicyGenerator />} />
          <Route path="/tools/inventory-builder" element={<InventoryBuilder />} />
          <Route path="/tools/governance-checklist" element={<GovernanceChecklist />} />
          
          {/* ========== ARTICLE CMS ROUTES ========== */}
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/:slug" element={<ArticleDetail />} />
          
          {/* Legacy Legal Pages */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/certification-scope" element={<CertificationScope />} />
          <Route path="/eligibility" element={<Eligibility />} />
          <Route path="/enforcement" element={<Enforcement />} />
          <Route path="/revocation" element={<Revocation />} />
          <Route path="/withdrawal-policy" element={<Revocation />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/docs" element={<Documentation />} />

          {/* ========== STANDARDS ROUTES (with MainLayout) ========== */}
          <Route path="/standards" element={<WithLayout component={StandardsIndexPage} />} />
          <Route path="/standards/ai-abnd-1-0" element={<WithLayout component={StandardAIABND} />} />
          <Route path="/standards/ai-hitl-1-0" element={<WithLayout component={StandardAIHITL} />} />
          <Route path="/standards/ai-acct-1-0" element={<WithLayout component={StandardAIACCT} />} />
          <Route path="/standards/ai-read-1-0" element={<WithLayout component={StandardAIREAD} />} />
          <Route path="/standards/ai-perf-1-0" element={<WithLayout component={StandardAIPERF} />} />
          <Route path="/standards/ai-roi-1-0" element={<WithLayout component={StandardAIROI} />} />
          <Route path="/standards/ai-disc-1-0" element={<WithLayout component={StandardAIDISC} />} />
          <Route path="/standards/accountability-boundaries-packet-template" element={<WithLayout component={AccountabilityBoundariesPacketTemplate} />} />
          <Route path="/standards-taxonomy" element={<WithLayout component={StandardsTaxonomy} />} />
          <Route path="/standards-versioning-policy" element={<WithLayout component={StandardsVersioningPolicy} />} />
          <Route path="/standards-revision-policy" element={<WithLayout component={StandardsRevisionAndUpdatePolicy} />} />
          <Route path="/standards-alignment-and-principles" element={<WithLayout component={StandardsAlignmentAndPrinciples} />} />
          <Route path="/archived-standards" element={<WithLayout component={ArchivedStandards} />} />
          <Route path="/framework-alignment" element={<WithLayout component={FrameworkAlignment} />} />

          {/* ========== DECLARATION AND REGISTRY ROUTES (with MainLayout) ========== */}
          <Route path="/what-is-declaration" element={<WithLayout component={WhatIsDeclaration} />} />
          <Route path="/declaration-for-clients" element={<WithLayout component={WhatCertificationMeansForClients} />} />
          <Route path="/declaration-for-buyers" element={<WithLayout component={WhatCertificationMeansForBuyers} />} />
          <Route path="/declaration-program" element={<WithLayout component={DeclarationPage} />} />
          <Route path="/programs-and-pricing" element={<WithLayout component={PricingAndPrograms} />} />
          <Route path="/eligibility-requirements" element={<WithLayout component={EligibilityRequirements} />} />
          <Route path="/registry-status-definitions" element={<WithLayout component={RegistryStatusDefinitions} />} />
          <Route path="/registry-relationship" element={<WithLayout component={RegistryRelationship} />} />
          <Route path="/registry-methodology" element={<WithLayout component={RegistryMethodologyAndStatusDefinitions} />} />
          <Route path="/verify-credential" element={<WithLayout component={VerifyCredential} />} />
          <Route path="/mark-usage-policy" element={<WithLayout component={MarkUsagePolicy} />} />
          <Route path="/declaration-badge-usage-policy" element={<WithLayout component={DeclarationBadgeUsagePolicy} />} />
          <Route path="/submit-a-concern" element={<WithLayout component={SubmitAConcern} />} />
          <Route path="/enforcement-revocation-policy" element={<WithLayout component={EnforcementRevocationPolicy} />} />
          <Route path="/declaration-enforcement" element={<WithLayout component={DeclarationEnforcementAndStatus} />} />
          <Route path="/suspension-and-revocation-policy" element={<WithLayout component={SuspensionAndRevocationPolicy} />} />

          {/* ========== GOVERNANCE ROUTES (with MainLayout) ========== */}
          <Route path="/about" element={<WithLayout component={AboutPage} />} />
          <Route path="/governance-and-authority" element={<WithLayout component={GovernanceAndAuthority} />} />
          <Route path="/conflict-of-interest-policy" element={<WithLayout component={ConflictOfInterestPolicy} />} />
          <Route path="/independence-statement" element={<WithLayout component={IndependenceStatement} />} />
          <Route path="/declaration-scope-and-limitations" element={<WithLayout component={DeclarationScopeAndLimitations} />} />
          <Route path="/scope-of-applicability" element={<WithLayout component={ScopeOfApplicability} />} />
          <Route path="/why-these-standards-exist" element={<WithLayout component={WhyTheseStandardsExist} />} />
          <Route path="/terms-of-declaration" element={<WithLayout component={TermsOfDeclaration} />} />
          <Route path="/terms-of-service" element={<WithLayout component={TermsOfService} />} />
          <Route path="/privacy-policy" element={<WithLayout component={PrivacyPolicyHorizons} />} />
          <Route path="/using-declaration-in-proposals" element={<WithLayout component={UsingDeclarationInProposals} />} />
          <Route path="/declaration-faqs" element={<WithLayout component={DeclarationFAQs} />} />
          <Route path="/methodology" element={<WithLayout component={MethodologyPage} />} />
          <Route path="/standards-alignment-declaration" element={<WithLayout component={StandardsAlignmentDeclaration} />} />
          <Route path="/what-is-alignment-declaration" element={<WithLayout component={WhatIsAlignmentDeclaration} />} />

          {/* ========== REDIRECT ROUTES (Old Horizons URLs) ========== */}
          <Route path="/certification" element={<Navigate to="/declaration-program" replace />} />
          <Route path="/level-1-standards-alignment-declaration" element={<Navigate to="/declaration-program" replace />} />
          <Route path="/level-2-aasc-certification" element={<Navigate to="/declaration-program" replace />} />
          <Route path="/tier-1-baseline-certification" element={<Navigate to="/declaration-program" replace />} />
          <Route path="/tier-1-certification-requirements" element={<Navigate to="/declaration-program" replace />} />
          <Route path="/tier-1-certification-checklist" element={<Navigate to="/declaration-program" replace />} />
          <Route path="/certification-page" element={<Navigate to="/apply" replace />} />
          <Route path="/declare-alignment" element={<Navigate to="/apply" replace />} />
          <Route path="/certification-faqs" element={<Navigate to="/declaration-faqs" replace />} />
          <Route path="/certification-enforcement-and-status" element={<Navigate to="/declaration-enforcement" replace />} />
          <Route path="/certification-scope-and-limitations" element={<Navigate to="/declaration-scope-and-limitations" replace />} />
          <Route path="/mark-and-badge-usage-policy" element={<Navigate to="/declaration-badge-usage-policy" replace />} />
          <Route path="/what-is-certification" element={<Navigate to="/what-is-declaration" replace />} />
          <Route path="/what-certification-means-for-clients" element={<Navigate to="/declaration-for-clients" replace />} />
          <Route path="/what-certification-means-for-buyers" element={<Navigate to="/declaration-for-buyers" replace />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

          {/* Organization Routes */}
          <Route path="/org/login" element={<OrgLogin />} />
          <Route path="/org/dashboard" element={<OrgDashboard />} />
          <Route path="/org" element={<Navigate to="/org/dashboard" replace />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* Axiom AI Assistant - Available on all pages */}
        <AxiomChat />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
